<div class="flex flex-wrap gap-10 justify-center"
     [class.z-10]="isMultiSelectMode"
     [class.relative]="isMultiSelectMode">
  @if (!documents || documents.length === 0) {
    <div class="text-gray-500 italic p-5">
      {{ 'animalDocuments.noDocuments' | transloco }}
    </div>
  }
  @for (item of documents; track item.id) {
    <div
      class="w-[15.5rem] h-96 perspective-[1000px] relative rounded-sm border-2"
      (click)="toggleSelect.emit(item.id)"
      tabindex="0"
      role="button"
      [ngClass]="{
        'border-(--p-primary-color)': isSelected(item.id),
        'border-transparent': !isSelected(item.id),
        'cursor-pointer': isMultiSelectMode
      }"
    >
      <div class="relative w-full h-full [transform-style:preserve-3d] transition-transform duration-700"
           [class.rotate-y-180]="flippedItems[item.id]">
        <!-- Vorderseite -->
        <div class="absolute inset-0 rounded-lg bg-white p-4 shadow z-2 backface-hidden flex flex-col">
          <div class="absolute top-1/2 -translate-y-1/2 right-[-1.75rem] w-8 h-14 backface-hidden">
            <p-button
              styleClass="w-full h-full bg-white rounded shadow flex items-center justify-center text-xl text-primary"
              (click)="toggleFlip.emit({id: item.id, event: $event})"
              [class.cursor-default]="isMultiSelectMode"
              [attr.aria-label]="'animalDocuments.aria.goToBack' | transloco"
              icon="pi pi-chevron-right"
              variant="text"
            />
          </div>

          <!-- Button für Maximieren / Vorschau -->
          <div class="absolute top-2 right-2 z-10 backface-hidden">
            <p-button
              icon="pi pi-window-maximize"
              styleClass="p-button-rounded p-button-text bg-white/80"
              (click)="openPreviewDialog(item, $event)"
              [attr.aria-label]="'animalDocuments.aria.preview' | transloco"
            ></p-button>
          </div>

          <div class="text-sm text-gray-500 text-center mb-[10px]">{{ 'animalDocuments.labels.category' | transloco }}</div>
          <div class="text-green-700 text-center font-bold mb-2 h-[46px]">
            {{ ('animal.documentCategory.' + item.category) | transloco }}
          </div>
          <div class="relative w-full flex-1 flex flex-col items-center justify-center gap-2">
            <i class="{{ fileMeta(item).icon }} text-6xl {{ fileMeta(item).colorClass }}" (click)="openPreviewDialog(item, $event)"></i>
            <span class="text-xs text-gray-600 text-center break-all px-2">{{ fileName(item) }}</span>
            <p-tag
              [severity]="isUpload(item) ? 'info' : 'warn'"
              [value]="(isUpload(item) ? 'animalDocuments.sourceType.upload' : 'animalDocuments.sourceType.generated') | transloco"
            />
          </div>
          <div class="mt-[5px] px-2">
            @if (isUpload(item)) {
              <p-datepicker
                [(ngModel)]="item.uiDate"
                [showIcon]="true"
                iconDisplay="input"
                [showOnFocus]="false"
                dateFormat="dd.mm.yy"
                styleClass="document-date-picker"
                appendTo="body"
                (onSelect)="dateChange.emit(item)"
              ></p-datepicker>
            } @else {
              <div class="text-center text-sm text-gray-500">
                {{ item.uiDate ? (item.uiDate | date:'dd.MM.yyyy') : '-' }}
              </div>
            }
          </div>
        </div>

        <!-- Rückseite -->
        <div class="absolute inset-0 rounded-lg bg-white p-4 shadow [transform:rotateY(180deg)] backface-hidden">
          <div class="absolute top-1/2 -translate-y-1/2 right-[-1.75rem] w-8 h-14 backface-hidden">
            <p-button
              styleClass="w-full h-full bg-white rounded shadow flex items-center justify-center text-xl text-primary"
              (click)="toggleFlip.emit({id: item.id, event: $event})"
              [class.cursor-default]="isMultiSelectMode"
              [attr.aria-label]="'animalDocuments.aria.goToFront' | transloco"
              icon="pi pi-chevron-left"
              variant="text"
            />
          </div>

          @if (isUpload(item)) {
            <div class="text-sm text-gray-500 mb-2">{{ 'animalDocuments.labels.category' | transloco }}</div>
            <p-select
              [options]="categoryOptions"
              [(ngModel)]="item.category"
              optionLabel="name"
              optionValue="value"
              appendTo="body"
              styleClass="w-full"
              (onChange)="categoryChange.emit(item)"
            />
            <div class="flex flex-col mt-4 gap-1 items-stretch">
              <p-button
                [label]="'animalDocuments.item.btnReplace' | transloco"
                icon="pi pi-upload"
                styleClass="w-full"
                (click)="replaceInput.click()"
              />
              <input
                #replaceInput
                type="file"
                [accept]="acceptTypes"
                class="hidden"
                (change)="onReplaceFileSelected(item, $event)"
              />
              <p-button
                [label]="'animalDocuments.item.btnDownload' | transloco"
                icon="pi pi-download"
                styleClass="w-full"
                (click)="downloadDocument.emit(item)"
              />
              <p-button
                [label]="'animalDocuments.item.btnDelete' | transloco"
                icon="pi pi-trash"
                severity="danger"
                (click)="deleteDocument.emit(item)"
                styleClass="w-full"
              />
            </div>
          } @else {
            <div class="text-sm text-gray-500 mb-2">{{ 'animalDocuments.labels.info' | transloco }}</div>
            <div class="flex flex-col gap-1 text-sm text-gray-700">
              <div>
                <span class="font-semibold">{{ 'animalDocuments.labels.category' | transloco }}:</span>
                {{ ('animal.documentCategory.' + item.category) | transloco }}
              </div>
              <div>
                <span class="font-semibold">{{ 'animalDocuments.labels.date' | transloco }}:</span>
                {{ item.uiDate ? (item.uiDate | date:'dd.MM.yyyy') : '-' }}
              </div>
              <div class="text-xs text-gray-400 italic mt-1">
                {{ 'animalDocuments.generatedHint' | transloco }}
              </div>
            </div>
            <div class="flex flex-col mt-4 gap-1 items-stretch">
              <p-button
                [label]="'animalDocuments.item.btnDownload' | transloco"
                icon="pi pi-download"
                styleClass="w-full"
                (click)="downloadDocument.emit(item)"
              />
            </div>
          }
        </div>
      </div>
    </div>
  }
</div>

<!-- Dialog für PDF-Vorschau -->
<p-dialog
  [(visible)]="previewDialogVisible"
  [modal]="true"
  [closable]="true"
  [maximizable]="true"
  [blockScroll]="true"
  [dismissableMask]="true"
  [style]="{width: '80vw', height: '85vh'}"
  (onHide)="closePreviewDialog()"
>
  <ng-template pTemplate="header">
    <span>{{ selectedDocument ? (('animal.documentCategory.' + selectedDocument.category) | transloco) : '-' }}</span>
  </ng-template>

  <div class="relative h-full w-full">
    @if (previewAvailable && previewUrl) {
      <iframe
        [src]="previewUrl"
        class="w-full h-[70vh] border-0"
        [title]="'animalDocuments.aria.preview' | transloco"
      ></iframe>
    } @else if (selectedDocument) {
      <div class="flex flex-col items-center justify-center gap-4 h-[70vh] text-center">
        <i class="{{ fileMeta(selectedDocument).icon }} text-7xl {{ fileMeta(selectedDocument).colorClass }}"></i>
        <span class="text-sm text-gray-600 break-all px-4">{{ fileName(selectedDocument) }}</span>
        <span class="text-gray-500">{{ 'animalDocuments.previewUnavailable' | transloco }}</span>
        <p-button
          [label]="'animalDocuments.item.btnDownload' | transloco"
          icon="pi pi-download"
          (click)="downloadDocument.emit(selectedDocument)"
        />
      </div>
    }
  </div>
</p-dialog>
